import { useCallback, useMemo, useState } from "react";
import { API } from "../../../../API";
import AlertButtonWrapper from "../../../Containers/AlertButtonWrapper";
import RectangleButtonCreatorPanel from "../../../Buttons/RectangleButtonCreatorPanel";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import {
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  DEFAULT_PRODUCT_TEXTS_CAMPAIGN_ID,
  NotificationType,
} from "@markit/common.types";

type AutomationDefaultApplyAllModalProps = {
  campaignId: string;
};

const AutomationDefaultApplyAllModal = (
  props: AutomationDefaultApplyAllModalProps
) => {
  const { campaignId } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [alertText, setAlertText] = useState("");

  const isDefaultEventTextsAutomation = useMemo(
    () => campaignId === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
    [campaignId]
  );

  const isDefaultProductTextsAutomation = useMemo(
    () => campaignId === DEFAULT_PRODUCT_TEXTS_CAMPAIGN_ID,
    [campaignId]
  );

  const updateAllUpcomingAutomations = useCallback(async () => {
    setLoadingUpdate(true);
    const response = await API.campaign.updateUpcomingEventAutomations({
      userId: accountData.uid,
      campaignId,
    });
    if (!response.success) {
      setAlertText("An error occurred. Please try again later");
    }
    showNotificationBanner(
      dispatch,
      response.success ? "Success!" : "Failed!",
      response.success
        ? NotificationType.AFFIRMATIVE
        : NotificationType.NEGATIVE
    );
    setLoadingUpdate(false);
  }, [accountData.uid, campaignId, dispatch]);

  return (
    <>
      <AlertButtonWrapper
        buttonComp={
          <RectangleButtonCreatorPanel
            title={`Apply to All ${
              isDefaultEventTextsAutomation
                ? "Events"
                : isDefaultProductTextsAutomation
                ? "Products"
                : "Forms"
            }`}
            iconName="ion:calendar"
            onPress={() => setConfirmVisible(true)}
            loading={loadingUpdate}
          />
        }
        alertTextHeader={alertText}
        clearAlert={() => setAlertText("")}
      />
      <ConfirmActionModal
        heading={`Overwrite and apply these default texts to all of your current ${
          isDefaultEventTextsAutomation
            ? "events"
            : isDefaultProductTextsAutomation
            ? "products"
            : "forms"
        }?`}
        subtext={`By pressing 'Confirm', this will update the default texts for all of your current ${
          isDefaultEventTextsAutomation
            ? "events"
            : isDefaultProductTextsAutomation
            ? "products"
            : "forms"
        }. This cannot be undone.`}
        confirmButtonText={"Confirm"}
        hideModal={!confirmVisible}
        setIsVisible={setConfirmVisible}
        confirmOnPress={updateAllUpcomingAutomations}
        cancelOnPress={() => setConfirmVisible(false)}
      />
    </>
  );
};

export default AutomationDefaultApplyAllModal;
